import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';

const UserProfileMenu = ({ compact = false, className = '' }) => {
  const { user, firebaseUser, userProfile, credits, logout } = useAuth();
  const { theme = 'dark', toggleTheme } = useContext(ThemeContext) || {};
  const { t } = useTranslation('common');
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [photoErrored, setPhotoErrored] = useState(false);

  const resolvedUser = user || firebaseUser;
  const profileDisplayName = userProfile?.profile?.display_name;
  const profileAvatar = userProfile?.profile?.avatar_url;

  const computeProviderPhoto = (targetUser) => {
    if (!targetUser?.providerData?.length) return null;
    const googleProvider = targetUser.providerData.find(
      (provider) => provider.providerId === 'google.com' && provider.photoURL
    );
    if (googleProvider?.photoURL) return googleProvider.photoURL;
    const providerWithPhoto = targetUser.providerData.find((provider) => !!provider.photoURL);
    return providerWithPhoto?.photoURL || null;
  };

  const sanitizePhotoUrl = (url) => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      if (!['http:', 'https:'].includes(parsed.protocol)) return null;
      return parsed.toString();
    } catch {
      return null;
    }
  };

  const rawPhoto =
    profileAvatar || resolvedUser?.photoURL || computeProviderPhoto(resolvedUser);
  const photo = useMemo(() => sanitizePhotoUrl(rawPhoto), [rawPhoto]);

  useEffect(() => {
    setPhotoErrored(false);
  }, [photo]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayPhoto = photo && !photoErrored ? photo : null;
  const name =
    profileDisplayName ||
    resolvedUser?.displayName ||
    userProfile?.email ||
    resolvedUser?.email ||
    t('user.default');
  const emailValue = userProfile?.email || resolvedUser?.email || '';
  const initial = (name && name[0] ? name[0] : 'U').toUpperCase();
  const isMuzoPlus =
    (userProfile?.plan?.status === 'active' && userProfile?.plan?.type === 'pro') ||
    userProfile?.saas_usage?.subscription_active;

  const closeMenu = () => setMenuOpen(false);
  const handleToggleTheme = () => {
    toggleTheme?.();
    closeMenu();
  };
  const handleNavigateSettings = () => {
    navigate('/settings');
    closeMenu();
  };
  const handleLogout = () => {
    logout();
    closeMenu();
  };

  const ActionButtons = () => (
    <div className="space-y-1 text-left">
      <button
        onClick={handleNavigateSettings}
        className={`w-full flex items-center gap-3 px-2 py-2 text-sm transition ${
          isDark ? 'text-white hover:bg-white/5' : 'text-slate-800 hover:bg-slate-100'
        }`}
      >
        <FontAwesomeIcon icon="cogs" className="text-base" />
        <span>{t('actions.settings')}</span>
      </button>
      <button
        onClick={handleToggleTheme}
        className={`w-full flex items-center gap-3 px-2 py-2 text-sm transition ${
          isDark ? 'text-white hover:bg-white/5' : 'text-slate-800 hover:bg-slate-100'
        }`}
      >
        <FontAwesomeIcon icon={isDark ? 'sun' : 'moon'} className="text-base" />
        <span>{t('actions.appearance')}</span>
        <span className="ml-auto text-xs text-slate-500">{isDark ? t('actions.dark') : t('actions.light')}</span>
      </button>
      <button
        onClick={handleLogout}
        className={`w-full flex items-center gap-3 px-2 py-2 text-sm transition ${
          isDark ? 'text-rose-200 hover:bg-rose-500/10' : 'text-rose-500 hover:bg-rose-50'
        }`}
      >
        <FontAwesomeIcon icon="power-off" className="text-base" />
        <span>{t('actions.logout')}</span>
      </button>
    </div>
  );

  const renderAvatar = (variant = 'md') => {
    const sizeClass =
      variant === 'lg' ? 'w-12 h-12' : variant === 'sm' ? 'w-9 h-9' : 'w-10 h-10';

    if (displayPhoto) {
      return (
        <img
          src={displayPhoto}
          alt={name}
          className={`${sizeClass} rounded-full object-cover`}
          onError={() => setPhotoErrored(true)}
        />
      );
    }
    return (
      <div className={`${sizeClass} rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white font-semibold flex items-center justify-center`}>
        {initial}
      </div>
    );
  };

  const PlanBadge = () =>
    isMuzoPlus ? (
      <span className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 text-[10px] font-bold px-3 py-1 shadow-sm uppercase tracking-wide">
        <FontAwesomeIcon icon="bolt" className="text-[11px]" />
        Muzo+
      </span>
    ) : null;

  if (compact) {
    return (
      <div ref={dropdownRef} className={`relative inline-flex justify-center w-full ${className}`}>
        <button
          onClick={() => setMenuOpen((prev) => !prev)}
          className={`group relative flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-full transition-all duration-300 ${
            isDark
              ? 'bg-slate-800 text-white hover:bg-indigo-600 hover:shadow-[0_0_15px_rgba(79,70,229,0.4)]'
              : 'bg-white text-slate-900 hover:bg-indigo-50 hover:text-indigo-600 shadow-sm border border-slate-200'
          } ${
            menuOpen
              ? `ring-2 ring-indigo-500 ring-offset-2 ${
                  isDark ? 'ring-offset-slate-900' : 'ring-offset-white'
                } scale-105`
              : ''
          }`}
          aria-label="Menu utilisateur"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          {renderAvatar('sm')}
        </button>

        {/* Sexy Floating Menu */}
        <div
          className={`absolute bottom-0 left-full ml-5 w-72 rounded-2xl shadow-2xl backdrop-blur-xl border transition-all duration-300 origin-bottom-left z-50 overflow-hidden ${
            menuOpen
              ? 'opacity-100 scale-100 translate-x-0 translate-y-0'
              : 'opacity-0 scale-95 -translate-x-4 translate-y-4 pointer-events-none'
          } ${
            isDark
              ? 'bg-slate-900/90 border-white/10 text-white'
              : 'bg-white/90 border-slate-200 text-slate-800'
          }`}
        >
          {/* Decorative background glow */}
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-indigo-500/20 to-transparent pointer-events-none" />

          {/* Header Section */}
          <div className="relative p-5 pb-4">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="ring-2 ring-white/10 rounded-full p-0.5">
                  {renderAvatar('md')}
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-base truncate leading-tight">{name}</h3>
                  <p className="text-xs opacity-60 truncate mt-0.5">{emailValue}</p>
                </div>
              </div>
            </div>

            {/* Stats / Plan Card */}
            <div
              className={`flex items-center justify-between p-3 rounded-xl border ${
                isDark
                  ? 'bg-white/5 border-white/5'
                  : 'bg-slate-50 border-slate-100'
              }`}
            >
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wider opacity-50 font-bold mb-0.5">
                  Solde
                </span>
                <div className="flex items-center gap-2 text-sm font-bold text-amber-400">
                  <FontAwesomeIcon icon="coins" />
                  <span>{credits ?? 0}</span>
                </div>
              </div>
              {isMuzoPlus ? (
                <div className="flex flex-col items-end">
                  <span className="text-[10px] uppercase tracking-wider opacity-50 font-bold mb-0.5">
                    Plan
                  </span>
                  <PlanBadge />
                </div>
              ) : (
                <button className="text-xs font-semibold text-indigo-400 hover:text-indigo-300 transition-colors">
                  Passer à Muzo+ &rarr;
                </button>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className={`h-px w-full ${isDark ? 'bg-white/10' : 'bg-slate-100'}`} />

          {/* Actions List */}
          <div className="p-2">
            <ActionButtons />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      {/* Modern Drop-up Menu */}
      <div
        className={`absolute bottom-[calc(100%+0.75rem)] left-0 w-full rounded-xl shadow-2xl border backdrop-blur-md overflow-hidden transition-all duration-300 origin-bottom z-50 ${
          menuOpen
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-2 pointer-events-none'
        } ${
          isDark
            ? 'bg-slate-900/95 border-white/10 text-white'
            : 'bg-white/95 border-slate-200 text-slate-800'
        }`}
      >
        <div
          className={`p-3 border-b ${
            isDark ? 'border-white/5 bg-white/5' : 'border-slate-100 bg-slate-50'
          }`}
        >
          <p className="text-sm font-bold truncate">{name}</p>
          {emailValue && <p className="text-xs opacity-60 truncate">{emailValue}</p>}
        </div>
        <div className="p-1">
          <ActionButtons />
        </div>
      </div>

      {/* Compact Trigger Button */}
      <button
        type="button"
        onClick={() => setMenuOpen((prev) => !prev)}
        className={`group relative flex w-full items-center gap-3 p-2 rounded-xl transition-all duration-200 border ${
          isDark
            ? 'bg-white/5 border-transparent hover:bg-white/10 hover:border-white/10 text-white'
            : 'bg-white border-slate-200 hover:border-indigo-200 hover:shadow-md text-slate-900'
        } ${menuOpen ? 'ring-2 ring-indigo-500/50 border-transparent' : ''}`}
      >
        {renderAvatar('md')}

        <div className="flex-1 min-w-0 text-left flex flex-col justify-center gap-0.5">
          <p className="text-sm font-bold truncate leading-tight">{name}</p>

          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3 text-xs opacity-70">
              <span className="flex items-center gap-1.5 font-medium">
                <FontAwesomeIcon icon="coins" className="text-[10px] text-amber-400" />
                <span>{credits ?? 0}</span>
              </span>
            </div>
            {isMuzoPlus && (
              <div className="scale-75 origin-right flex-shrink-0">
                <PlanBadge />
              </div>
            )}
          </div>
        </div>

        <FontAwesomeIcon
          icon="chevron-up"
          className={`text-[10px] opacity-40 transition-transform duration-300 group-hover:opacity-100 ${
            menuOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
    </div>
  );
};

export default UserProfileMenu;
